/**
 * An amount: quick choices plus a numeric pad. Confirming sends what the props say — words
 * (`{amount}` is replaced) or a button with the amount in its data.
 */
import { useState } from 'react';
import { z } from 'zod';
import { defineWidget } from '../contract';
import { Icon } from '../../ui-kit/Icon';
import { Panel } from '../../ui-kit/Panel';
import css from '../Widgets.module.css';

const schema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  icon: z.string().optional(),
  unit: z.string().default('€'),
  quick: z.array(z.number().positive()).max(6).default([]),
  max: z.number().positive().optional(),
  confirmLabel: z.string(),
  submit: z.union([
    z.object({ say: z.string().includes('{amount}') }),
    z.object({ press: z.object({ button: z.string(), field: z.string() }) }),
  ]),
});

export const keypad = defineWidget({
  key: 'keypad',
  schema,
  Component: function KeypadWidget({ props, api }) {
    const [digits, setDigits] = useState('');
    const amount = Number(digits || '0');
    const tooMuch = props.max !== undefined && amount > props.max;
    const send = (value: number) => {
      if (value <= 0) return;
      if ('say' in props.submit) api.say(props.submit.say.replace('{amount}', String(value)));
      else api.press(props.submit.press.button, { [props.submit.press.field]: value });
      setDigits('');
    };
    return (
      <Panel title={props.title} subtitle={props.subtitle} icon={props.icon}>
        <div className={css.keypad}>
          <div className={css.quick}>
            {props.quick.map((q) => (
              <button key={q} className={css.tile} onClick={() => send(q)}>
                <span className={css.tileLabel}>{q} {props.unit}</span>
              </button>
            ))}
          </div>
          <div className={css.pad}>
            <output className={`${css.display} ${tooMuch ? css.invalid : ''}`}>{digits || '0'} {props.unit}</output>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} className={css.padKey} onClick={() => setDigits((v) => (v + d).replace(/^0+/, '').slice(0, 5))}>{d}</button>
            ))}
            <button className={css.padKey} onClick={() => setDigits((v) => v.slice(0, -1))} aria-label="delete"><Icon name="delete" size={36} /></button>
            <button className={css.padKey} onClick={() => setDigits((v) => (v ? `${v}0`.slice(0, 5) : v))}>0</button>
            <button className={`${css.padKey} ${css.padOk}`} onClick={() => send(amount)} disabled={amount <= 0 || tooMuch}
              aria-label={props.confirmLabel}>
              <Icon name="check" size={40} />
            </button>
          </div>
        </div>
      </Panel>
    );
  },
});
